import React, {useMemo} from 'react';

import {pluralize, sum} from 'BenchlingUtil';
import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataFrame, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {numericAccessor} from 'data-frames/util/AccessorUtils';
import aggregateRows from 'data-frames/util/aggregateRows';
import {createAccessor, DataFrameValueAccessor} from 'data-frames/util/createAccessors';
import sortDataFrame from 'data-frames/util/sortDataFrame';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {
  AnalyticsVisualizationAxisScale,
  AnalyticsVisualizationBarChartType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import makeVisualizationConfig from 'insights/universal-blocks/makeVisualizationConfig';
import {VisualizationType} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';

import {
  getBaseEntitiesToBaseFeaturesMap,
  makeParameterOptimizationFeature,
} from '../ParameterOptimizationUtils';
import ModelingDashboardBlock, {SharedModelingBlockProps} from './ModelingDashboardBlock';
import {PARAMETER_COLUMN_DEF} from './ModelingDashboardConstants';

export const FEATURE_IMPORTANCE_BLOCK_TITLE = 'Parameter importance';
// TODO(BNCH-47779): Add 'Click on a bar to drill down in "Feature contribution"' to the description once we support
// click drill downs.
const FEATURE_IMPORTANCE_BLOCK_DESCRIPTION =
  "The parameters are ranked from most to least important based on how much they factored into the model's prediction. Importance is always positive.";

/** @package */
export const VISUALIZATION_TYPE = VisualizationType.BAR_CHART;
/** @package */
export const BASE_VISUALIZATION_PARAMETERS = {
  aggregationType: null,
  barChartType: AnalyticsVisualizationBarChartType.STANDARD,
  errorBarType: null,
  sortDirection: null,
  seriesColumnName: null,
  yAxisMin: null,
  yAxisMax: null,
  yAxisScale: AnalyticsVisualizationAxisScale.LINEAR,
};

/** @package */
export const MAX_FEATURES_TO_VISUALIZE = 20;

/** @package */
export const IMPORTANCE_COLUMN_DEF: DataFrameColumnDef = {
  systemName: 'importance',
  displayName: 'Importance',
  type: {
    leafType: LeafType.DECIMAL,
  },
};

function aggregateImportanceByBaseFeatures(
  dataFrame: DataFrame,
  baseEntitiesAccessor: DataFrameValueAccessor<Array<string>>,
  baseFeaturesAccessor: DataFrameValueAccessor<Array<string>>,
  importanceAccessor: DataFrameValueAccessor<number>
): DataFrame {
  const baseEntitiesToBaseFeatureMap = getBaseEntitiesToBaseFeaturesMap(
    dataFrame,
    baseEntitiesAccessor,
    baseFeaturesAccessor
  );

  return aggregateRows(
    dataFrame,
    (row) =>
      makeParameterOptimizationFeature(
        baseEntitiesAccessor(row)[0],
        baseFeaturesAccessor(row)[0],
        baseEntitiesToBaseFeatureMap
      ).displayName,
    (rows, partitionValue) => ({
      [PARAMETER_COLUMN_DEF.systemName]: partitionValue,
      // The current metric for `importance` is always positive, but we run `abs()` because that might change.
      [IMPORTANCE_COLUMN_DEF.systemName]: String(sum(rows.map((row) => Math.abs(importanceAccessor(row))))),
    }),
    [PARAMETER_COLUMN_DEF, IMPORTANCE_COLUMN_DEF]
  );
}

function aggregateLessImportantFeatures(dataFrame: DataFrame): DataFrame {
  const lessImportantFeatures = dataFrame.rows.slice(MAX_FEATURES_TO_VISUALIZE);
  const lessImportantFeaturesLabel = `Remaining ${lessImportantFeatures.length} ${pluralize(
    lessImportantFeatures.length,
    'feature'
  )}`;

  const importanceAccessor = numericAccessor(
    createAccessor(dataFrame, IMPORTANCE_COLUMN_DEF.systemName).accessor,
    IMPORTANCE_COLUMN_DEF.type.leafType
  );

  const lessImportantFeatureSummary = {
    [PARAMETER_COLUMN_DEF.systemName]: lessImportantFeaturesLabel,
    [IMPORTANCE_COLUMN_DEF.systemName]: String(
      sum(lessImportantFeatures.map((row) => importanceAccessor(row)))
    ),
    [SystemColumnName.IS_EXCLUDED]: false,
    [SystemColumnName.ROW_ID]: lessImportantFeaturesLabel,
  };

  return {
    ...dataFrame,
    rows: [
      ...dataFrame.rows.slice(0, MAX_FEATURES_TO_VISUALIZE),
      ...(lessImportantFeatures.length > 0 ? [lessImportantFeatureSummary] : []),
    ],
  };
}

/**
 * Given a feature importance data frame, return a transformed data frame used to render in the block's visualization
 *
 * To generate the visualization data frame, we do the following:
 *   1. Aggregate the importance of the features with the same `ParameterOptimizationFeature`
 *   2. Name the feature by it's `ParameterOptimizationFeature`'s `displayName`
 *   3. Sort the features by importance
 *   4. Aggregate every feature except the best `MAX_FEATURES_TO_VISUALIZE` features if there are more than
 *      `MAX_FEATURES_TO_VISUALIZE` features
 *
 * @package
 */
export function makeFeatureImportanceVisualizationDataFrame(
  dataFrame: DataFrame,
  baseEntitiesAccessor: DataFrameValueAccessor<Array<string>>,
  baseFeaturesAccessor: DataFrameValueAccessor<Array<string>>,
  importanceAccessor: DataFrameValueAccessor<number>
): DataFrame {
  const aggregatedDataFrame = aggregateImportanceByBaseFeatures(
    dataFrame,
    baseEntitiesAccessor,
    baseFeaturesAccessor,
    importanceAccessor
  );

  const sortedDataFrame = sortDataFrame(aggregatedDataFrame, {
    columnKey: 'importance',
    sortDirection: 'arrow-down',
  });

  return aggregateLessImportantFeatures(sortedDataFrame);
}

/** @package */
export interface FeatureImportanceBlockProps extends SharedModelingBlockProps {
  dataFrame: DataFrame;
  baseEntitiesAccessor: DataFrameValueAccessor<Array<string>>;
  baseFeaturesAccessor: DataFrameValueAccessor<Array<string>>;
  importanceAccessor: DataFrameValueAccessor<number>;
}

export default function FeatureImportanceBlock({
  dataFrame,
  baseEntitiesAccessor,
  baseFeaturesAccessor,
  importanceAccessor,
  ...sharedModelingBlockProps
}: FeatureImportanceBlockProps): JSX.Element {
  const visualizationConfig = makeVisualizationConfig(VISUALIZATION_TYPE, {
    ...BASE_VISUALIZATION_PARAMETERS,
    xAxisColumnName: PARAMETER_COLUMN_DEF.systemName,
    yAxisColumnName: IMPORTANCE_COLUMN_DEF.systemName,
  });

  const visualizationFeatureImportance = useMemo(
    () =>
      makeFeatureImportanceVisualizationDataFrame(
        dataFrame,
        baseEntitiesAccessor,
        baseFeaturesAccessor,
        importanceAccessor
      ),
    [dataFrame, baseEntitiesAccessor, baseFeaturesAccessor, importanceAccessor]
  );

  return (
    <ModelingDashboardBlock
      {...sharedModelingBlockProps}
      blockTitle={FEATURE_IMPORTANCE_BLOCK_TITLE}
      blockDescription={FEATURE_IMPORTANCE_BLOCK_DESCRIPTION}
      inputDataFrame={visualizationFeatureImportance}
      visualizationConfig={visualizationConfig}
    />
  );
}
